import React, { useEffect, useState, useCallback } from 'react';
import { Drawer, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import apiAxios from 'helpers/apiAxios';
import { STATUS_COLORS } from './WaiverStatusChart';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const [date] = String(iso).split('T');
  const [y, m, d] = date.split('-');
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d}-${MONTHS[parseInt(m, 10) - 1]}-${y}`;
};

const PER_PAGE = 25;

// Slide-over showing the detailed records behind any chart segment.
// extraParams narrows baseParams (e.g. { statuses: [3] }, { cse_incharges: ['ARY'] },
// { start_date, end_date } for a trend period, { overdue: 'true' } for aging buckets).
const WaiverDrillDownPanel = ({ open, title, baseParams, extraParams, onClose }) => {
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback((p) => {
    setLoading(true);
    apiAxios.get('/crm/dashboard/waivers/get_detailed_data', {
      params: { ...baseParams, ...extraParams, page: p, per_page: PER_PAGE },
    })
      .then(res => {
        setRows(res.data?.rows || []);
        setTotal(res.data?.total_rows || 0);
      })
      .catch(() => { setRows([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [baseParams, extraParams]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    fetchRows(1);
  }, [open, fetchRows]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const drawerTitle = (
    <div>
      <p className="tw-text-sm tw-font-bold tw-text-gray-900 tw-mb-0">{title}</p>
      <p className="tw-text-xs tw-text-gray-400 tw-font-normal tw-mb-0">{total.toLocaleString()} waivers</p>
    </div>
  );

  const footer = total > PER_PAGE ? (
    <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
      <span className="tw-text-xs tw-text-gray-400">Page {page} / {totalPages}</span>
      <div className="tw-flex tw-items-center tw-gap-1.5">
        <button type="button" disabled={page <= 1 || loading}
          onClick={() => { const p = page - 1; setPage(p); fetchRows(p); }}
          className="tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-text-xs tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-text-gray-600 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed hover:tw-bg-gray-50 tw-cursor-pointer">
          <FiChevronLeft size={12} /> Prev
        </button>
        <button type="button" disabled={page >= totalPages || loading}
          onClick={() => { const p = page + 1; setPage(p); fetchRows(p); }}
          className="tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1 tw-text-xs tw-rounded tw-border tw-border-gray-200 tw-bg-white tw-text-gray-600 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed hover:tw-bg-gray-50 tw-cursor-pointer">
          Next <FiChevronRight size={12} />
        </button>
      </div>
    </div>
  ) : null;

  return (
    <Drawer
      show={open}
      onHide={onClose}
      title={drawerTitle}
      footer={footer}
      width="min(560px, 100vw)"
      minWidth="340px"
    >
      <div>
        {loading ? (
            <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="md" /></div>
          ) : rows.length === 0 ? (
            <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-10">No records</p>
          ) : (
            <div className="tw-divide-y tw-divide-gray-50">
              {rows.map(r => (
                <div key={r.id} className="tw-px-5 tw-py-3 hover:tw-bg-slate-50 tw-transition-colors">
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1">
                    <span>
                      {r.deal_id ? (
                        <Link to={`/crm/projects/${r.deal_id}#waiver`} target="_blank" rel="noreferrer"
                          className="tw-font-mono tw-font-bold tw-text-blue-700 hover:tw-underline tw-text-xs">
                          {r.waiver_number}
                        </Link>
                      ) : <span className="tw-font-mono tw-font-bold tw-text-gray-700 tw-text-xs">{r.waiver_number}</span>}
                      {r.j_number && <span className="tw-text-[11px] tw-text-gray-400 tw-ml-1.5">{r.j_number}</span>}
                    </span>
                    <span className="tw-flex tw-items-center tw-gap-1.5">
                      {r.overdue && (
                        <span className="tw-text-[10px] tw-font-bold tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-bg-orange-100 tw-text-orange-600">Overdue</span>
                      )}
                      <span className="tw-text-[10px] tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-xl"
                        style={{ background: `${STATUS_COLORS[r.status_id] || '#94A3B8'}1f`, color: STATUS_COLORS[r.status_id] || '#64748B' }}>
                        {r.status}
                      </span>
                    </span>
                  </div>
                  <p className="tw-text-xs tw-text-gray-700 tw-truncate tw-mb-0.5" title={r.project_name}>{r.project_name || '—'}</p>
                  <p className="tw-text-[11px] tw-text-gray-400 tw-mb-0">
                    Commitment: {fmtDate(r.commitment_date)} · CSE: {(r.cse_incharge || []).join(', ') || '—'} · {r.unit_name}
                  </p>
                </div>
              ))}
            </div>
          )}
      </div>
    </Drawer>
  );
};

WaiverDrillDownPanel.propTypes = {
  open: PropTypes.bool, title: PropTypes.string,
  baseParams: PropTypes.object, extraParams: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};
WaiverDrillDownPanel.defaultProps = { open: false, title: '', baseParams: {}, extraParams: {} };

export default WaiverDrillDownPanel;
